// Pure helpers for the Data Management page: grouping, severity order, and
// the upload checks the page runs before a file leaves the browser.
import type {
  DataIssue,
  DatasetSchema,
  IssueSeverity,
  TableStatus,
} from "@dashboard-intelligent/api/src/modules/industry/types";
import type { BadgeTone, ConsoleGlyphName } from "@dashboard-intelligent/ui";
import { formatMetric } from "../../format";

export type TableKind = TableStatus["kind"];

export const KIND_ORDER: TableKind[] = ["dimension", "shared-fact", "industry-fact"];

export const KIND_TEXT: Record<TableKind, string> = {
  dimension: "Dimensi",
  "shared-fact": "Fakta bersama",
  "industry-fact": "Fakta industri",
};

const SEVERITY_RANK: Record<IssueSeverity, number> = { high: 0, medium: 1, low: 2 };

export const SEVERITY: Record<IssueSeverity, { text: string; tone: BadgeTone; icon: ConsoleGlyphName }> = {
  high: { text: "Berat", tone: "danger", icon: "attention" },
  medium: { text: "Sedang", tone: "warning", icon: "warning" },
  low: { text: "Ringan", tone: "neutral", icon: "info" },
};

/** Worst first; within one severity, the larger count first. */
export function sortIssues(issues: DataIssue[]): DataIssue[] {
  return [...issues].sort((a, b) => SEVERITY_RANK[a.severity] - SEVERITY_RANK[b.severity] || b.count - a.count);
}

/** Tables by kind, in KIND_ORDER, empty kinds left out. */
export function groupByKind<T extends { kind: TableKind }>(tables: T[]): { kind: TableKind; tables: T[] }[] {
  return KIND_ORDER.map((kind) => ({ kind, tables: tables.filter((table) => table.kind === kind) })).filter(
    (group) => group.tables.length > 0,
  );
}

/** 0 to 1 completeness as "93,4%". */
export function share(value: number): string {
  return formatMetric(value * 100, "percent");
}

/** 26214400 as "25 MB". */
export function bytesText(bytes: number): string {
  if (bytes < 1024 * 1024) return `${formatMetric(bytes / 1024, "decimal")} KB`;
  return `${formatMetric(bytes / (1024 * 1024), "decimal")} MB`;
}

/** [".json", ".csv"] as "JSON atau CSV"; three or more as "A, B, atau C". */
export function acceptsText(accepts: string[]): string {
  const names = accepts.map((ext) => ext.replace(/^\./, "").toUpperCase());
  if (names.length <= 2) return names.join(" atau ");
  return `${names.slice(0, -1).join(", ")}, atau ${names[names.length - 1]}`;
}

/** The input's accept attribute. A double extension also lists its last part, which every picker understands. */
export function acceptAttr(accepts: string[]): string {
  const extra = accepts.filter((ext) => ext.split(".").length > 2).map((ext) => `.${ext.split(".").pop()}`);
  return [...new Set([...accepts, ...extra])].join(",");
}

export function matchesAccept(filename: string, accepts: string[]): boolean {
  const name = filename.toLowerCase();
  return accepts.some((ext) => name.endsWith(ext.toLowerCase()));
}

/** A spreadsheet or CSV holds one table; a .json(.gz) holds a whole dataset. */
export function isSingleTableFile(filename: string): boolean {
  return /\.(csv|xlsx)$/i.test(filename);
}

/** The table choice only exists when the schema has more than one table to replace. */
export function needsTableChoice(schema: DatasetSchema): boolean {
  return schema.tables.length > 1;
}

/** "1 Oktober 2026 pukul 08.00", in the reader's own time zone. */
export function loadedAtText(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return iso;
  return new Intl.DateTimeFormat("id-ID", { dateStyle: "long", timeStyle: "short" }).format(date);
}
